import { create } from "zustand";
import { Track } from "@/types/track";
import { SearchFilters } from "@/types/search-filters";
import {
  SupabasePlaylistService,
  PlaylistWithTracks,
} from "./supabase-playlists";
import { listReferencePlaylists } from "./reference-imports";
import {
  mergeReferencePlaylists,
  filterPlaylistsByTab,
  isDiscoImport,
  type PlaylistTab,
} from "./playlists-disco";
import {
  clearPlaylistVideo,
  writePlaylistVideo,
  writePlaylistVideoLink,
  type PlaylistSearchMetadata,
} from "./playlist-video";
import { copyPlaylistCover } from "./playlist-cover";

export type ViewMode = "grid" | "list";

/**
 * Newest first: the initial order and what Clear All restores. Alphabetical was the default,
 * which dropped a just-created playlist wherever its name happened to fall, usually out of sight.
 */
export const DEFAULT_PLAYLIST_SORT = {
  sortBy: "date-created",
  sortOrder: "desc",
} as const;

// Updated to use string IDs for Supabase compatibility
export interface Playlist {
  id: string;
  title: string;
  description?: string;
  coverUrl?: string | null;
  tracks: Track[];
  createdAt: string;
  updatedAt: string;
  isPublic: boolean;
  createdBy?: string; // User ID who created the playlist
  /** "disco_import" for Disco-imported playlists; undefined for regular playlists. */
  source?: string;
  /** Import pipeline status (disco_import playlists only). */
  import_status?: string | null;
  // Search metadata for AI-generated playlists
  searchMetadata?: {
    query?: string;
    audioAnalysis?: string;
    videoAnalysis?: string;
    audioSummary?: string;
    videoSummary?: string;
    tags?: string[]; // Deprecated: kept for backwards compatibility
    filters?: SearchFilters; // New: structured filter object
    // File references for preview functionality (for creation)
    videoFile?: File;
    audioFile?: File;
    /** The reference video's path in the `media` bucket. Read it through `playlistVideoPath`. */
    videoPath?: string;
    /** Legacy: a 7-day signed URL, stored before videos were kept by path. Read for its path. */
    videoUrl?: string;
    /** A YouTube link from Sol's Save results, canonical. Read it through `playlistVideoLink`. */
    videoLink?: string;
    // URL for a stored file (from database)
    audioUrl?: string;
  };
}

/**
 * What a duplicate actually produced. The tracks are saved in one insert, so `copiedTracks` is all
 * of them or none, and `playlist.tracks` holds exactly what was saved.
 */
export interface DuplicatePlaylistResult {
  playlist: Playlist;
  copiedTracks: number;
  totalTracks: number;
  /** The original has a cover, and the copy couldn't get a file of its own, so it has none. */
  coverLost: boolean;
}

interface PlaylistsState {
  playlists: Playlist[];
  filteredPlaylists: Playlist[];
  selectedPlaylistId: string | null;
  isDetailsOpen: boolean;
  isSelectionMode: boolean;
  selectedPlaylistIds: Set<string>;
  /** Playlists being duplicated right now. Duplicate is off for them until the copy is made. */
  duplicatingIds: ReadonlySet<string>;
  viewMode: ViewMode;
  isLoading: boolean;
  isLoadPlaylistsInProgress: boolean; // Prevent multiple simultaneous loads
  searchQuery: string;
  sortBy: string;
  sortOrder: "asc" | "desc";
  activeFilters: Set<string>;
  activeTab: PlaylistTab;
  setViewMode: (mode: ViewMode) => void;
  setActiveTab: (tab: PlaylistTab) => void;
  setPlaylists: (playlists: Playlist[]) => void;
  loadPlaylists: () => Promise<void>;
  selectPlaylist: (id: string) => void;
  closeDetails: () => void;
  toggleSelectionMode: () => void;
  togglePlaylistSelection: (id: string) => void;
  clearSelection: () => void;
  clearPlaylistSelection: () => void;
  /**
   * Create a playlist and place it in the lists. Throws when it can't be created, with the
   * service's error, so the caller can say why (`getUserErrorMessage`).
   */
  createPlaylist: (
    data: Omit<Playlist, "id" | "createdAt" | "updatedAt">
  ) => Promise<Playlist>;
  updatePlaylist: (id: string, data: Partial<Playlist>) => Promise<void>;
  deletePlaylist: (id: string) => Promise<void>;
  addTrackToPlaylist: (playlistId: string, track: Track) => Promise<boolean>;
  /**
   * Copy one of the user's playlists, with its tracks and a cover file of its own, into a new
   * private playlist. Null when nothing was created: the playlist is unknown, is a Disco import,
   * is already being duplicated (`duplicatingIds`), or the copy could not be created.
   */
  duplicatePlaylist: (playlistId: string) => Promise<DuplicatePlaylistResult | null>;
  /**
   * Upload `file` as the playlist's reference video, replacing any video it had. The stored
   * search metadata is then patched into the playlist in place (F59), and returned. Throws when
   * the video can't be saved, and the lists are then unchanged.
   */
  attachPlaylistVideo: (id: string, file: File) => Promise<PlaylistSearchMetadata>;
  /**
   * Store a YouTube link as the playlist's video, replacing any video it had (Sol's Save results
   * only), patched in place and returned like `attachPlaylistVideo`. Throws when it can't be saved.
   */
  attachPlaylistVideoLink: (id: string, url: string) => Promise<PlaylistSearchMetadata>;
  /** Remove the playlist's reference video. Throws on failure, leaving the lists unchanged. */
  removePlaylistVideo: (id: string) => Promise<void>;
  removeTrackFromPlaylist: (
    playlistId: string,
    trackId: string
  ) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setSortBy: (sortBy: string) => void;
  setSortOrder: (order: "asc" | "desc") => void;
  toggleFilter: (filter: string) => void;
  clearFilters: () => void;
  applyFiltersAndSort: () => void;
  /**
   * Forget the signed-in account's playlists and everything done with them: the lists, the open
   * playlist, the selection, copies in progress, the loading flags, and the search, filters and
   * tab. resetAllStores calls it on sign-out and on an account switch. The view mode and the sort
   * stay: they are how this browser likes the list shown, not the account's data.
   */
  reset: () => void;
}

/** The state that decides which playlists the list shows, and in what order. */
export type PlaylistListCriteria = Pick<
  PlaylistsState,
  "playlists" | "activeTab" | "searchQuery" | "activeFilters" | "sortBy" | "sortOrder"
>;

function playlistSortKey(playlist: Playlist, sortBy: string): string | number {
  switch (sortBy) {
    case "date-created":
      return Date.parse(playlist.createdAt);
    case "last-modified":
      return Date.parse(playlist.updatedAt);
    case "track-count":
      return playlist.tracks.length;
    default: // "title", and any unknown key
      return playlist.title.toLowerCase();
  }
}

/**
 * The list as the playlists page shows it: the tab, then the search, then the filters, then the
 * sort. Pure, so every place that changes the list (load, create, duplicate, Clear All) produces
 * the same order the sort menu promises. Never mutates `criteria.playlists`.
 */
export function filterAndSortPlaylists(criteria: PlaylistListCriteria): Playlist[] {
  // Apply tab filter first (All / My Playlists / Disco Imports)
  let filtered = filterPlaylistsByTab([...criteria.playlists], criteria.activeTab ?? "all");

  // Apply search filter
  if (criteria.searchQuery.trim()) {
    const query = criteria.searchQuery.toLowerCase();
    filtered = filtered.filter(
      (playlist) =>
        playlist.title.toLowerCase().includes(query) ||
        playlist.description?.toLowerCase().includes(query) ||
        playlist.searchMetadata?.tags?.some((tag) =>
          tag.toLowerCase().includes(query)
        ) ||
        playlist.searchMetadata?.query?.toLowerCase().includes(query)
    );
  }

  // Apply active filters
  if (criteria.activeFilters.size > 0) {
    filtered = filtered.filter((playlist) => {
      if (criteria.activeFilters.has("public") && !playlist.isPublic)
        return false;
      if (criteria.activeFilters.has("private") && playlist.isPublic)
        return false;
      if (
        criteria.activeFilters.has("has-tracks") &&
        playlist.tracks.length === 0
      )
        return false;
      if (criteria.activeFilters.has("empty") && playlist.tracks.length > 0)
        return false;
      if (criteria.activeFilters.has("recently-modified")) {
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        if (new Date(playlist.updatedAt) < weekAgo) return false;
      }
      return true;
    });
  }

  // Apply sorting
  filtered.sort((a, b) => {
    const aValue = playlistSortKey(a, criteria.sortBy);
    const bValue = playlistSortKey(b, criteria.sortBy);
    if (criteria.sortOrder === "asc") {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    }
    return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
  });

  return filtered;
}

// Convert Supabase playlist to local format
function convertSupabasePlaylist(
  supabasePlaylist: PlaylistWithTracks
): Playlist {
  return {
    id: supabasePlaylist.id,
    title: supabasePlaylist.title,
    description: supabasePlaylist.description,
    coverUrl: supabasePlaylist.coverUrl,
    tracks: supabasePlaylist.tracks,
    createdAt: supabasePlaylist.createdAt,
    updatedAt: supabasePlaylist.updatedAt,
    isPublic: supabasePlaylist.isPublic,
    createdBy: supabasePlaylist.createdBy, // Include createdBy for ownership checks
    searchMetadata: supabasePlaylist.searchMetadata,
    source: supabasePlaylist.source,
    import_status: supabasePlaylist.import_status,
  };
}

/**
 * Both lists with one playlist's stored search metadata swapped in, and nothing else changed. The
 * entry is patched in place, not re-sorted, because a video changes nothing the list is sorted or
 * filtered by (the search query and tags are kept). `updatedAt` stays as loaded, since the video
 * write doesn't set `updated_at`.
 */
function withSearchMetadata(
  state: Pick<PlaylistsState, "playlists" | "filteredPlaylists">,
  id: string,
  searchMetadata: PlaylistSearchMetadata
): Pick<PlaylistsState, "playlists" | "filteredPlaylists"> {
  const patch = (playlist: Playlist): Playlist =>
    playlist.id === id
      ? // The stored JSON, as loadPlaylists passes it through.
        { ...playlist, searchMetadata: searchMetadata as Playlist["searchMetadata"] }
      : playlist;
  return {
    playlists: state.playlists.map(patch),
    filteredPlaylists: state.filteredPlaylists.map(patch),
  };
}

// Utility function to deduplicate playlists by ID
function deduplicatePlaylists(playlists: Playlist[]): Playlist[] {
  const seen = new Set<string>();
  return playlists.filter((playlist) => {
    if (seen.has(playlist.id)) {
      return false;
    }
    seen.add(playlist.id);
    return true;
  });
}

/**
 * Moves on at every reset(). A load or a create in flight from before it never writes into the
 * next account's lists: its playlists belong to the account that asked.
 */
let playlistsGeneration = 0;

export const usePlaylistsStore = create<PlaylistsState>((set, get) => ({
  playlists: [], // Start empty to prevent flash
  filteredPlaylists: [], // Start empty to prevent flash
  selectedPlaylistId: null,
  isDetailsOpen: false,
  isSelectionMode: false,
  selectedPlaylistIds: new Set(),
  duplicatingIds: new Set<string>(),
  viewMode: "grid",
  isLoading: true, // Start with loading state
  isLoadPlaylistsInProgress: false, // Prevent multiple simultaneous loads
  searchQuery: "",
  sortBy: DEFAULT_PLAYLIST_SORT.sortBy,
  sortOrder: DEFAULT_PLAYLIST_SORT.sortOrder,
  activeFilters: new Set(),
  activeTab: "all" as PlaylistTab,

  setViewMode: (mode) => set({ viewMode: mode }),

  setActiveTab: (tab) => {
    set({ activeTab: tab });
    // Re-apply filters/sort so filteredPlaylists updates immediately (synchronous — no flash).
    get().applyFiltersAndSort();
  },

  setPlaylists: (playlists) => {
    const deduplicatedPlaylists = deduplicatePlaylists(playlists);
    set({
      playlists: deduplicatedPlaylists,
      filteredPlaylists: deduplicatedPlaylists,
    });
  },

  // Load playlists from Supabase
  loadPlaylists: async () => {
    const currentState = get();

    // Prevent multiple simultaneous calls
    if (currentState.isLoadPlaylistsInProgress) {
      return;
    }

    set({
      isLoading: true,
      isLoadPlaylistsInProgress: true,
    });
    const generation = playlistsGeneration;

    try {
      // Fetch both sources in parallel: the user's own playlists (Supabase direct) AND
      // the org-scoped reference playlist list (backend API, needed for org-mate disco imports).
      // `getUserPlaylists` is per-user_id so org-mates' disco imports are invisible without the merge.
      const [supabasePlaylists, referenceResult] = await Promise.allSettled([
        SupabasePlaylistService.getUserPlaylists(),
        listReferencePlaylists({ limit: 200 }),
      ]);
      if (generation !== playlistsGeneration) return;

      const ownPlaylists =
        supabasePlaylists.status === "fulfilled"
          ? supabasePlaylists.value.map(convertSupabasePlaylist)
          : [];

      const refItems =
        referenceResult.status === "fulfilled"
          ? referenceResult.value.items
          : [];

      // Merge: append org-scoped disco imports not already in the user's own list.
      const merged = mergeReferencePlaylists(ownPlaylists, refItems);
      const finalPlaylists = deduplicatePlaylists(merged);

      set({
        playlists: finalPlaylists,
        // Sorted in the same set(): the merge appends org-mates' imports after the user's own
        // playlists, so showing it as-is ignored the sort the menu claimed.
        filteredPlaylists: filterAndSortPlaylists({ ...get(), playlists: finalPlaylists }),
        isLoading: false,
        isLoadPlaylistsInProgress: false,
      });
    } catch (error) {
      console.error("Error loading playlists:", error);
      if (generation !== playlistsGeneration) return;
      // Fall back to empty array if Supabase fails
      set({
        playlists: [],
        filteredPlaylists: [],
        isLoading: false,
        isLoadPlaylistsInProgress: false,
      });
    }
  },

  selectPlaylist: (id) =>
    set({
      selectedPlaylistId: id,
      isDetailsOpen: true,
    }),

  closeDetails: () =>
    set({
      isDetailsOpen: false,
    }),

  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedPlaylistIds: new Set(),
    })),

  togglePlaylistSelection: (id) =>
    set((state) => {
      const newSelection = new Set(state.selectedPlaylistIds);
      if (newSelection.has(id)) {
        newSelection.delete(id);
      } else {
        newSelection.add(id);
      }
      return { selectedPlaylistIds: newSelection };
    }),

  clearSelection: () => set({ selectedPlaylistIds: new Set() }),

  clearPlaylistSelection: () => set({ selectedPlaylistId: null }),

  createPlaylist: async (data) => {
    const generation = playlistsGeneration;
    // A failure is thrown on to the caller: the dialog that asked says why.
    const playlist = convertSupabasePlaylist(await SupabasePlaylistService.createPlaylist(data));
    // Made by an account that has since signed out: it is not the next account's playlist.
    if (generation !== playlistsGeneration) return playlist;
    set((state) => {
      // Check if playlist already exists to prevent duplicates
      const exists = state.playlists.find((p) => p.id === playlist.id);
      if (exists) {
        return state; // Don't add if already exists
      }

      // Placed by the active tab, search, filters and sort, like every other playlist
      // (newest first by default, so a new playlist lands at the top).
      const playlists = [playlist, ...state.playlists];
      return {
        playlists,
        filteredPlaylists: filterAndSortPlaylists({ ...state, playlists }),
      };
    });
    return playlist;
  },

  updatePlaylist: async (id, data) => {
    try {
      // Update in backend first
      const success = await SupabasePlaylistService.updatePlaylist(id, data);

      if (success) {
        // Update local state only if backend update succeeded
        set((state) => ({
          playlists: state.playlists.map((playlist) =>
            playlist.id === id
              ? {
                  ...playlist,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
          filteredPlaylists: state.filteredPlaylists.map((playlist) =>
            playlist.id === id
              ? {
                  ...playlist,
                  ...data,
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
        }));
      } else {
        throw new Error("Failed to update playlist in backend");
      }
    } catch (error) {
      console.error("Error updating playlist:", error);
      throw error; // Re-throw so the UI can handle it
    }
  },

  deletePlaylist: async (id) => {
    try {
      const success = await SupabasePlaylistService.deletePlaylist(id);

      if (success) {
        set((state) => ({
          playlists: state.playlists.filter((playlist) => playlist.id !== id),
          filteredPlaylists: state.filteredPlaylists.filter(
            (playlist) => playlist.id !== id
          ),
          selectedPlaylistId:
            state.selectedPlaylistId === id ? null : state.selectedPlaylistId,
          isDetailsOpen:
            state.selectedPlaylistId === id ? false : state.isDetailsOpen,
        }));
      }
    } catch (error) {
      console.error("Error deleting playlist:", error);
    }
  },

  addTrackToPlaylist: async (playlistId, track) => {
    try {
      const success = await SupabasePlaylistService.addTrackToPlaylist(
        playlistId,
        String(track.id)
      );

      if (success) {
        // Update local state
        set((state) => ({
          playlists: state.playlists.map((playlist) =>
            playlist.id === playlistId
              ? {
                  ...playlist,
                  tracks: [...playlist.tracks, track],
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
          filteredPlaylists: state.filteredPlaylists.map((playlist) =>
            playlist.id === playlistId
              ? {
                  ...playlist,
                  tracks: [...playlist.tracks, track],
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
        }));
        return true;
      } else {
        console.error(
          "Failed to add track to playlist - Supabase returned false"
        );
        return false;
      }
    } catch (error) {
      console.error("Error adding track to playlist:", error);
      return false;
    }
  },

  duplicatePlaylist: async (playlistId) => {
    const source = get().playlists.find((p) => p.id === playlistId);
    // A Disco import lists no catalog tracks here (its rows are reference tracks, loaded on its
    // own page), so its copy would be an empty playlist. The menus hide Duplicate for imports.
    if (!source || isDiscoImport(source)) return null;
    // One copy at a time per playlist: a second click (or a menu opened before the first copy
    // finished) must not make a second copy. Marked before the first await.
    if (get().duplicatingIds.has(playlistId)) return null;
    set((state) => ({ duplicatingIds: new Set(state.duplicatingIds).add(playlistId) }));

    try {
      // A plain private playlist: no `source` (it is not an import), no search metadata (that
      // points at the original's uploaded reference media), and private until its owner shares
      // it. No cover yet: see below.
      let copy: Playlist;
      try {
        copy = await get().createPlaylist({
          title: `${source.title} (copy)`,
          description: source.description,
          coverUrl: null,
          tracks: [],
          isPublic: false,
        });
      } catch (error) {
        console.error("Couldn't create the copy of the playlist:", error);
        return null;
      }

      // The original's cover file is overwritten in place whenever its cover changes, so the copy
      // gets a file of its own, once its row exists (playlist-cover.ts). If that fails, no cover.
      let coverUrl: string | null = null;
      if (source.coverUrl) {
        const copiedCover = await copyPlaylistCover(source.coverUrl, copy.id);
        if (
          copiedCover &&
          (await SupabasePlaylistService.updatePlaylist(copy.id, { coverUrl: copiedCover }))
        ) {
          coverUrl = copiedCover;
        }
      }

      // One insert, numbered in the original's order: the tracks land together or not at all,
      // and the copy keeps exactly what was saved, so the caller can say when it has none.
      const saved = await SupabasePlaylistService.addTracksToEmptyPlaylist(
        copy.id,
        source.tracks.map((track) => String(track.id))
      );
      const copied: Track[] = saved ? source.tracks : [];

      const playlist: Playlist = {
        ...copy,
        coverUrl,
        tracks: copied,
        updatedAt: new Date().toISOString(),
      };
      set((state) => {
        const playlists = state.playlists.map((p) => (p.id === playlist.id ? playlist : p));
        // Re-filtered, not patched in place: the copy was placed while it had no tracks, which is
        // the wrong slot under "Most tracks" and the wrong side of the Empty / Has Tracks filters.
        return { playlists, filteredPlaylists: filterAndSortPlaylists({ ...state, playlists }) };
      });

      return {
        playlist,
        copiedTracks: copied.length,
        totalTracks: source.tracks.length,
        coverLost: Boolean(source.coverUrl) && coverUrl === null,
      };
    } finally {
      set((state) => {
        const duplicatingIds = new Set(state.duplicatingIds);
        duplicatingIds.delete(playlistId);
        return { duplicatingIds };
      });
    }
  },

  attachPlaylistVideo: async (id, file) => {
    try {
      const searchMetadata = await writePlaylistVideo(id, file);
      set((state) => withSearchMetadata(state, id, searchMetadata));
      return searchMetadata;
    } catch (error) {
      console.error("Error attaching the playlist's video:", error);
      throw error; // Re-thrown so the dialog can say what went wrong
    }
  },

  attachPlaylistVideoLink: async (id, url) => {
    try {
      const searchMetadata = await writePlaylistVideoLink(id, url);
      set((state) => withSearchMetadata(state, id, searchMetadata));
      return searchMetadata;
    } catch (error) {
      console.error("Error saving the playlist's video link:", error);
      throw error; // Re-thrown so the caller can say what went wrong
    }
  },

  removePlaylistVideo: async (id) => {
    try {
      const searchMetadata = await clearPlaylistVideo(id);
      set((state) => withSearchMetadata(state, id, searchMetadata));
    } catch (error) {
      console.error("Error removing the playlist's video:", error);
      throw error; // Re-thrown so the dialog can say what went wrong
    }
  },

  removeTrackFromPlaylist: async (playlistId, trackId) => {
    try {
      const success = await SupabasePlaylistService.removeTrackFromPlaylist(
        playlistId,
        trackId
      );

      if (success) {
        // Update local state
        set((state) => ({
          playlists: state.playlists.map((playlist) =>
            playlist.id === playlistId
              ? {
                  ...playlist,
                  tracks: playlist.tracks.filter(
                    (track) => track.id !== trackId
                  ),
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
          filteredPlaylists: state.filteredPlaylists.map((playlist) =>
            playlist.id === playlistId
              ? {
                  ...playlist,
                  tracks: playlist.tracks.filter(
                    (track) => track.id !== trackId
                  ),
                  updatedAt: new Date().toISOString(),
                }
              : playlist
          ),
        }));
      }
    } catch (error) {
      console.error("Error removing track from playlist:", error);
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),

  setSortBy: (sortBy) => set({ sortBy }),

  setSortOrder: (order) => set({ sortOrder: order }),

  toggleFilter: (filter) =>
    set((state) => {
      const newFilters = new Set(state.activeFilters);
      if (newFilters.has(filter)) {
        newFilters.delete(filter);
      } else {
        newFilters.add(filter);
      }
      return { activeFilters: newFilters };
    }),

  clearFilters: () =>
    set((state) => {
      const cleared = {
        activeFilters: new Set<string>(),
        searchQuery: "",
        sortBy: DEFAULT_PLAYLIST_SORT.sortBy,
        sortOrder: DEFAULT_PLAYLIST_SORT.sortOrder,
        activeTab: "all" as PlaylistTab,
      };
      // The list is re-applied here: Clear All's only other re-apply is the debounced search,
      // which never fires when the search box was already empty, so clearing a filter alone
      // left the list filtered.
      return { ...cleared, filteredPlaylists: filterAndSortPlaylists({ ...state, ...cleared }) };
    }),

  applyFiltersAndSort: () => {
    set({ filteredPlaylists: filterAndSortPlaylists(get()) });
  },

  reset: () => {
    playlistsGeneration += 1;
    set({
      playlists: [],
      filteredPlaylists: [],
      selectedPlaylistId: null,
      isDetailsOpen: false,
      isSelectionMode: false,
      selectedPlaylistIds: new Set<string>(),
      duplicatingIds: new Set<string>(),
      isLoading: true,
      isLoadPlaylistsInProgress: false,
      searchQuery: "",
      activeFilters: new Set<string>(),
      activeTab: "all",
    });
  },
}));
